import type { z } from 'zod';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** The Zod schemas of one route. Only the server holds them at runtime. */
export type RouteSchemas = {
  body?: z.ZodType;
  query?: z.ZodType;
  response: z.ZodType;
};

/**
 * One API route, shared by server and web. The object holds only method and
 * path, so the web bundle carries no schemas; the types come from the
 * schemas through the type parameter. The server registers the route with
 * the matching schemas, and a changed response shape fails `pnpm typecheck`
 * in the web app.
 */
export type RouteDef<M extends HttpMethod = HttpMethod, P extends string = string, S extends RouteSchemas = RouteSchemas> = {
  readonly method: M;
  readonly path: P;
  /** Status code of a successful response. Defaults to 200. */
  readonly status?: number;
  /** Type-only marker; never set at runtime. */
  readonly __schemas?: S;
};

export function defineRoute<S extends RouteSchemas>() {
  return <M extends HttpMethod, P extends string>(def: { method: M; path: P; status?: number }): RouteDef<M, P, S> => def;
}

type ParamNames<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | ParamNames<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? Name
    : never;

export type RouteParams<P extends string> = [ParamNames<P>] extends [never] ? undefined : Record<ParamNames<P>, string>;

type SchemasOf<D> = D extends RouteDef<HttpMethod, string, infer S> ? S : never;
type InputOf<T> = T extends z.ZodType ? z.input<T> : undefined;

export type RouteBody<D> = InputOf<SchemasOf<D>['body']>;
export type RouteQuery<D> = InputOf<SchemasOf<D>['query']>;
export type RouteResponse<D> = z.output<SchemasOf<D>['response']>;
export type RoutePath<D> = D extends RouteDef<HttpMethod, infer P> ? P : string;

/** Fills `:name` placeholders in a path. */
export function buildPath(path: string, params?: Record<string, string>): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_, name: string) => {
    const value = params?.[name];
    if (value === undefined) throw new Error(`Missing path parameter ${name}`);
    return encodeURIComponent(value);
  });
}
